/**
 * WhatsAppCard.jsx
 *
 * Per-repo WhatsApp panel (opened from Configure Repository → Enable WhatsApp).
 * Just two states:
 *   1. A number is connected for this repo → show the number + linked repo +
 *      a Disconnect action.
 *   2. No number → show the "Connect Number" flow (the add-number window).
 *
 * There is deliberately no per-number "settings/save" panel: a number is bound
 * to exactly one repo at connect time, so there's nothing to edit here — you
 * either have it connected, or you connect/disconnect it.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Config } from 'config/envConfig';
import { alert, AlertType, Button, LoadingView } from '@quyva/shared-ui';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { faPlus, faCircle, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getUrl } from '../../utils';
import { WhatsAppConnectFlow } from './WhatsAppConnectFlow';

const fetchNumbers = async (user, orgId) => {
    const url = getUrl(
        `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/whatsapp/numbers/${orgId}`
    );
    const resp = await axios.get(url, {
        headers: { Authorization: encodeURIComponent(user.token) },
    });
    return resp.data.numbers || [];
};

export const WhatsAppCard = ({ user, orgId, contextRepo }) => {
    const [numbers,     setNumbers]     = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [showConnect, setShowConnect] = useState(false);

    const loadNumbers = useCallback(async () => {
        try {
            setLoading(true);
            const data = await fetchNumbers(user, orgId);
            setNumbers(data);
        } catch (err) {
            alert(AlertType.ERROR, 'Failed to load WhatsApp numbers: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, [user, orgId]);

    useEffect(() => { loadNumbers(); }, [loadNumbers]);

    const handleConnectSuccess = () => {
        setShowConnect(false);
        loadNumbers();
        alert(AlertType.SUCCESS, 'WhatsApp number connected successfully!');
    };

    const handleDisconnect = async (phoneId) => {
        if (!window.confirm('Disconnect this WhatsApp number? This will stop all message routing for it.')) return;
        try {
            const url = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/whatsapp/disconnect/${phoneId}`
            );
            await axios.delete(url, {
                headers: { Authorization: encodeURIComponent(user.token) },
            });
            alert(AlertType.SUCCESS, 'Number disconnected.');
            loadNumbers();
        } catch (err) {
            alert(AlertType.ERROR, 'Failed to disconnect: ' + err.message);
        }
    };

    if (loading) return <LoadingView text="Loading WhatsApp…" />;

    // ── Add-number window ─────────────────────────────────────────────────────
    if (showConnect) {
        return (
            <WhatsAppConnectFlow
                user={user}
                orgId={orgId}
                contextRepo={contextRepo}
                onSuccess={handleConnectSuccess}
                onCancel={() => setShowConnect(false)}
            />
        );
    }

    // In the per-repo flow show only this repo's number. One number per repo.
    const displayNumbers = contextRepo
        ? numbers.filter((n) => String(n.repo_id) === String(contextRepo.repoid))
        : numbers;
    const connected = displayNumbers[0];

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2.5">
                    <FontAwesomeIcon icon={faWhatsapp} className="text-[28px] text-texts-tertiary400" />
                    <h3 className="m-0 text-base font-semibold text-texts-secondary">
                        {contextRepo ? `WhatsApp — ${contextRepo.name}` : 'WhatsApp Business'}
                    </h3>
                </div>
                {!connected && (
                    <Button
                        text="Connect Number"
                        icon={faPlus}
                        type="primary"
                        hoverText="Link a WhatsApp Business number"
                        onClick={() => setShowConnect(true)}
                    />
                )}
            </div>

            {/* State 1: no number → empty state */}
            {!connected ? (
                <div className="text-center text-texts-secondary300 py-10">
                    <FontAwesomeIcon icon={faWhatsapp} className="text-5xl text-backgrounds-border mb-3" />
                    <p className="m-0">
                        {contextRepo
                            ? 'No WhatsApp number connected for this repo yet.'
                            : 'No WhatsApp numbers connected yet.'}
                    </p>
                    <p className="text-sm mt-1">Click "Connect Number" to link a WhatsApp Business number.</p>
                </div>
            ) : (
                /* State 2: connected number + linked repo + Disconnect */
                <div className="flex items-center justify-between border border-backgrounds-border rounded-lg p-4 bg-backgrounds-white">
                    <div className="flex items-center gap-3">
                        <FontAwesomeIcon
                            icon={faCircle}
                            className={`text-[10px] ${
                                connected.is_active ? 'text-texts-tertiary400' : 'text-texts-secondary300'
                            }`}
                        />
                        <div>
                            <div className="text-sm font-semibold text-texts-secondary">
                                {connected.display_number}
                            </div>
                            <div className="mt-0.5 text-xs text-texts-secondary300">
                                {contextRepo
                                    ? `Linked to ${contextRepo.name}`
                                    : `Linked to repo #${connected.repo_id}`}
                            </div>
                        </div>
                    </div>
                    <Button
                        text="Disconnect"
                        type="danger"
                        hoverText="Disconnect and unbind this number"
                        onClick={() => handleDisconnect(connected.phone_number_id)}
                    />
                </div>
            )}

            {contextRepo && (
                <div className="w-full flex items-start text-sm mt-4">
                    <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="mr-2 mt-0.5 text-texts-tertiary400"
                        title="Information"
                    />
                    <span className="text-texts-secondary300">
                        A WhatsApp number is bound to{' '}
                        <span className="font-semibold text-texts-secondary">{contextRepo.name}</span>{' '}
                        and answers DMs from it. Disconnect to unbind and remove it.
                    </span>
                </div>
            )}
        </div>
    );
};

export default WhatsAppCard;
